import { playbackRates } from '@utils/constants/playback-rates.ts';
import { useAudio } from '@utils/providers/audio-context.tsx';
import { formatTime } from '@utils/audio.ts';
import TranscriptMediaSlider from '@components/editors/transcript-editor/footer/transcript-media-slider.tsx';
import { useState } from 'react';
import cn from 'classnames';
import SharedTranscriptTimecode from '@components/editors/shared-editor/shared-transcript/shared-transcript-timecode.tsx';
import { Session } from '@utils/types/editors/sessions.ts';
import { Clock, Link2, Pause, Play, Redo2, Unlink2, Undo2 } from 'lucide-react';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import { Tooltip } from '@components/atoms/ui-kit/tooltip.tsx';
import { useTranslation } from 'react-i18next';
import { shortcutLabel } from '@utils/editors/shortcut-label.tsx';

type Props = { session: Session | undefined };
export const SharedTranscriptPlayer = ({ session }: Props) => {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();
  const [timeCodeOpen, setTimeCodeOpen] = useState<boolean>(false);

  const {
    play,
    pause,
    isPlaying,
    rewind,
    forward,
    setPlaybackRate,
    currentTime,
    playbackRate,
    linkedPlayback,
    toggleLinkedPlayback
  } = useAudio();

  const iconBtnClass =
    'h-8 w-8 flex items-center justify-center transition-colors hover:opacity-80 rounded-2xl border shrink-0 disabled:opacity-40 disabled:cursor-not-allowed';
  const iconBtnStyle = {
    borderColor: getColour('border'),
    backgroundColor: getColour('thirdLayer'),
    color: getColour('icon')
  };

  return (
    <>
      <div
        className="flex items-center w-full h-12 px-2 gap-2 md:gap-3 overflow-x-auto"
        style={{ backgroundColor: getColour('secondLayer') }}
      >
        <div
          className={cn(
            'flex items-center gap-1 md:gap-2 shrink-0',
            !session?.media_url && 'opacity-50 pointer-events-none'
          )}
        >
          <div className="flex items-center gap-1">
            <Tooltip
              label={shortcutLabel(t('rewind-5-seconds'), 'Ctrl+← / F7')}
            >
              <button
                type="button"
                onClick={() => rewind()}
                disabled={!session?.media_url}
                aria-label={t('rewind-5-seconds')}
                className={iconBtnClass}
                style={iconBtnStyle}
              >
                <Undo2 size={14} />
              </button>
            </Tooltip>
            <Tooltip
              label={shortcutLabel(
                isPlaying ? t('pause') : t('play'),
                'Space / F5'
              )}
            >
              <button
                type="button"
                onClick={isPlaying ? pause : play}
                disabled={!session?.media_url}
                aria-label={isPlaying ? t('pause') : t('play')}
                className={iconBtnClass}
                style={iconBtnStyle}
              >
                {isPlaying ? <Pause size={14} /> : <Play size={14} />}
              </button>
            </Tooltip>
            <Tooltip
              label={shortcutLabel(t('forward-5-seconds'), 'Ctrl+→ / F8')}
            >
              <button
                type="button"
                onClick={() => forward()}
                disabled={!session?.media_url}
                aria-label={t('forward-5-seconds')}
                className={iconBtnClass}
                style={iconBtnStyle}
              >
                <Redo2 size={14} />
              </button>
            </Tooltip>
          </div>

          <Tooltip
            label={shortcutLabel(
              linkedPlayback
                ? t('linked-playback-on')
                : t('linked-playback-off'),
              'Ctrl+L'
            )}
          >
            <button
              type="button"
              onClick={toggleLinkedPlayback}
              disabled={!session?.keep_media}
              aria-label={
                linkedPlayback
                  ? t('linked-playback-on')
                  : t('linked-playback-off')
              }
              className={iconBtnClass}
              style={{
                ...iconBtnStyle,
                color: linkedPlayback
                  ? getColour('activeText')
                  : getColour('icon')
              }}
            >
              {linkedPlayback ? <Link2 size={14} /> : <Unlink2 size={14} />}
            </button>
          </Tooltip>

          <Tooltip label={t('timecodes')}>
            <button
              type="button"
              onClick={() => setTimeCodeOpen(true)}
              disabled={!session?.keep_media}
              aria-label={t('timecodes')}
              className={cn(iconBtnClass, 'hidden sm:flex')}
              style={iconBtnStyle}
            >
              <Clock size={14} />
            </button>
          </Tooltip>

          <select
            id="sharedPlaybackRate"
            value={playbackRate}
            onChange={e => setPlaybackRate(Number(e.target.value))}
            disabled={!session?.keep_media}
            className="h-8 px-2 text-xs rounded-2xl border cursor-pointer appearance-none text-center"
            style={{
              backgroundColor: getColour('thirdLayer'),
              borderColor: getColour('border'),
              color: getColour('normalText')
            }}
          >
            {playbackRates.map(rate => (
              <option key={rate.value} value={rate.value}>
                {rate.label}
              </option>
            ))}
          </select>

          <span
            className="text-xs font-medium tabular-nums whitespace-nowrap"
            style={{ color: getColour('inactiveText') }}
          >
            {formatTime(currentTime)}
          </span>
        </div>

        <div className="flex-1 min-w-[100px]">
          <TranscriptMediaSlider />
        </div>
      </div>

      {timeCodeOpen && (
        <SharedTranscriptTimecode setTimeCodeOpen={setTimeCodeOpen} />
      )}
    </>
  );
};
